'use client';

import localFont from 'next/font/local';
import { createTheme } from '@mui/material/styles';

const circular = localFont({
  src: '../../public/fonts/CircularStd-Book.woff2',
  display: 'swap',
});

export const circularBold = localFont({
  src: '../../public/fonts/CircularStd-Bold.woff2',
  display: 'swap',
});

export default createTheme({
  palette: {
    primary: {
      main: '#2C3239',
      light: '#9E9E9E',
    },
    secondary: {
      main: '#1399D6',
    },
    background: {
      default: '#ffffff',
    },
  },
  typography: {
    fontFamily: circular.style.fontFamily,
    h1: {
      fontFamily: circularBold.style.fontFamily,
    },
    h2: {
      fontFamily: circularBold.style.fontFamily,
    },
    h3: {
      fontFamily: circularBold.style.fontFamily,
    },
    h4: {
      fontFamily: circularBold.style.fontFamily,
    },
    h5: {
      fontFamily: circularBold.style.fontFamily,
    },
    h6: {
      fontFamily: circularBold.style.fontFamily,
    },
    button: {
      fontFamily: circularBold.style.fontFamily,
    },
    subtitle1: {
      fontFamily: circularBold.style.fontFamily,
    },
    subtitle2: {
      fontFamily: circularBold.style.fontFamily,
    },
    body1: {
      fontSize: '0.875rem',
    },
    body2: {
      fontSize: '0.875rem',
      color: '#9E9E9E',
    },
    caption: {
      color: '#757575',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 430,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          minHeight: '600px',
          height: '100dvh',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          borderBottomWidth: '2px',
          fontWeight: 700,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        wrapped: {
          textTransform: 'initial',
          fontWeight: 700,
        },
        fullWidth: {
          borderBottom: '2px solid #e0e0e0',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#424242',
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginBottom: 0,
          marginLeft: '-0.5rem',
        },
        label: {
          fontSize: '0.875rem',
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          minHeight: '2rem',
        },
        sizeSmall: {
          height: '2rem',
          width: '2rem',
          '&$extended': {
            height: '2rem',
          },
          backgroundColor: 'white',
          '&:hover': {
            backgroundColor: 'white',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          width: '100%',
        },
        multiline: {
          padding: '0.5rem',
          fontSize: '0.875rem',
          lineHeight: '1.5rem',
        },
        input: {
          padding: '0.75rem 1rem',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        standard: {
          padding: '0.75rem 1rem',
          width: '100%',
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          padding: '0.75rem 1rem',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: '2px',
          padding: '0.5rem 1.5rem',
          textTransform: 'none',
        },
        outlinedSizeSmall: {
          padding: '3px 8px',
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          zIndex: 1200,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          zIndex: 1250,
          padding: '1.25rem',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '1.25rem',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '0 1.25rem 1.25rem 1.25rem',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '1.25rem',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: '2.5rem',
          height: '1.5rem',
          padding: 0,
        },
        thumb: {
          width: '1.25rem',
          height: '1.25rem',
          marginTop: '1px',
          border: 'none',
          boxShadow: 'none',
        },
        switchBase: {
          padding: 1,
          '&$checked': {
            transform: 'translateX(1rem)',
          },
        },
        track: {
          borderRadius: '0.75rem',
          backgroundColor: '#bbb',
          opacity: 1,
          '$checked$checked + &': {
            opacity: 1,
            backgroundColor: '#2ECC71',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: '2rem',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          paddingTop: 0,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        paragraph: {
          marginBottom: '0.5rem',
        },
        h6: {
          fontSize: '1rem',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontWeight: 700,
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: '#f5f5f5',
          minHeight: 0,
          padding: '0.625rem 1rem',
          '&$expanded': {
            minHeight: 0,
          },
        },
        content: {
          margin: 0,
          '&$expanded': {
            margin: 0,
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: '1rem',
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          fontFamily: '',
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: '0.25rem 0.5rem',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.875rem',
        },
      },
    },
  },
});
